// src/components/tasks/TaskDetail/TaskDetailActions.tsx
// 任務詳情底部操作區

import React from 'react';
import { Task } from '../../../types/task';
import { Calendar, MoreHorizontal, RotateCcw } from '../../common/icons';
import { SmartSlice } from '../../common/icons';
import { TaskActionsSheet } from '../TaskActionsSheet';
import { TaskActionsPopover } from '../TaskActionsPopover';

interface TaskDetailActionsProps {
  task: Task;
  onSchedule: () => void;
  onSplit: () => void;
  onToggleStatus: () => void;
  onDrop: () => void;
  onDelete: () => void;
  showActionsMenu: boolean;
  onToggleActionsMenu: () => void;
  onCloseActionsMenu: () => void;
  actionsButtonRef: React.RefObject<HTMLButtonElement>;
}

export const TaskDetailActions: React.FC<TaskDetailActionsProps> = ({
  task,
  onSchedule,
  onSplit,
  onToggleStatus,
  onDrop,
  onDelete,
  showActionsMenu,
  onToggleActionsMenu,
  onCloseActionsMenu,
  actionsButtonRef
}) => {
  if (task.status === 'OPEN') {
    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
        {/* 行動版：三按鈕同一行 */}
        <div className="flex gap-3 sm:hidden">
          {/* Schedule - Accent */}
          <button onClick={onSchedule} className="btn-accent flex-1">
            <Calendar size={26} />
            <span>Schedule</span>
          </button>

          {/* Split - Primary Outline Icon-only */}
          <div className="relative group">
            <button
              onClick={onSplit}
              className="btn-primary-outline w-14 h-14 p-0"
              aria-label="Split task"
            >
              <SmartSlice size={26} />
            </button>
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-tooltip">
              Split
            </div>
          </div>

          {/* More - Ghost Icon-only */}
          <div className="relative">
            <button
              ref={actionsButtonRef}
              onClick={onToggleActionsMenu}
              className="w-14 h-14 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
              aria-label="More actions"
            >
              <MoreHorizontal size={26} />
            </button>

            <TaskActionsSheet
              isOpen={showActionsMenu}
              onClose={onCloseActionsMenu}
              onComplete={onToggleStatus}
              onDrop={onDrop}
              onDelete={onDelete}
            />
          </div>
        </div>

        {/* 桌面版：三個按鈕並排 */}
        <div className="hidden sm:flex sm:flex-1 sm:gap-3">
          {/* Schedule - Accent */}
          <button onClick={onSchedule} className="btn-accent flex-1">
            <Calendar size={18} />
            <span>Schedule</span>
          </button>

          {/* Split - Primary Outline Icon-only with tooltip */}
          <div className="relative group">
            <button
              onClick={onSplit}
              className="btn-primary-outline w-12 h-12 p-0"
              aria-label="Split task"
            >
              <SmartSlice size={20} />
            </button>
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-tooltip">
              Split
            </div>
          </div>

          {/* More Actions */}
          <div className="relative">
            <button
              ref={actionsButtonRef}
              onClick={onToggleActionsMenu}
              className="w-12 h-12 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
              aria-label="More actions"
            >
              <MoreHorizontal size={20} />
            </button>

            <TaskActionsPopover
              isOpen={showActionsMenu}
              onClose={onCloseActionsMenu}
              onComplete={onToggleStatus}
              onDrop={onDrop}
              onDelete={onDelete}
              anchorRef={actionsButtonRef}
            />
          </div>
        </div>
      </div>
    );
  }

  // CLOSED 狀態：只顯示 Reopen
  return (
    <div className="flex">
      <button
        onClick={onToggleStatus}
        className="flex-1 py-2.5 px-4 text-gray-600 hover:bg-gray-50 rounded-md transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
      >
        <RotateCcw size={16} />
        <span>Reopen</span>
      </button>
    </div>
  );
};
